import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Check, ChevronRight, Download, FileSpreadsheet } from "lucide-react"
import { toast } from "sonner"
import { STANDARD_FIELDS, type ContactImport } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Header, Hint, Section, SelectField } from "@/components/audience/primitives"
import { downloadCsv } from "@/lib/download"
import { api, ApiError } from "@/lib/api"
import { fieldsQuery, importQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/import/$importId/")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_customer_data"))
      throw redirect({ to: "/w/$workspace/contacts", params, search: {} })
  },
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(importQuery(params.workspace, params.importId)),
      context.queryClient.ensureQueryData(fieldsQuery(params.workspace)),
    ]),
  component: ImportMapping,
})

const BASE_TARGETS = [
  { value: "display_name", label: "Full name" },
  { value: "first_name", label: "First name" },
  { value: "email", label: "Email" },
  { value: "city", label: "City" },
  { value: "timezone", label: "Timezone" },
  { value: "instagram_handle", label: "Unverified Instagram handle" },
  { value: "tags", label: "Tags" },
  { value: "source_note", label: "Source note" },
  { value: "ignore", label: "Do not import" },
]
const RESTRICTED_TARGETS = [
  { value: "ignore", label: "Do not import" },
  { value: "source_note", label: "Source note" },
]

/** Screen 32, step 2: map columns. */
function ImportMapping() {
  const { workspace } = useWorkspaceContext()
  const { importId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const imported = useSuspenseQuery(importQuery(workspace.slug, importId)).data
  const fields = useSuspenseQuery(fieldsQuery(workspace.slug)).data.filter(
    (f) => f.origin === "workspace"
  )
  const [mapping, setMapping] = useState<Record<string, string>>(imported.mapping)
  const [reviewMatches, setReviewMatches] = useState(imported.options.reviewMatches)
  const params = { workspace: workspace.slug }
  const targets = [
    ...BASE_TARGETS.slice(0, 6),
    ...fields.map((f) => ({ value: `field:${f.key}`, label: f.label })),
    ...BASE_TARGETS.slice(6),
  ]

  const save = useMutation({
    mutationFn: () =>
      api<ContactImport>(`/w/${workspace.slug}/contacts/imports/${importId}`, {
        method: "PUT",
        body: JSON.stringify({ mapping, options: { reviewMatches } }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["workspace", workspace.slug, "imports", importId],
      })
      await navigate({
        to: "/w/$workspace/contacts/import/$importId/review",
        params: { ...params, importId },
      })
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.problem.title : "Could not validate the file"),
  })

  const mappedTargets = Object.values(mapping)
  const hasIdentity =
    mappedTargets.includes("display_name") ||
    mappedTargets.includes("email") ||
    mappedTargets.includes("first_name")

  return (
    <div className="page-wrap au-root">
      <Header
        title="Import contacts"
        description="Bring your contact details together, with a review before import."
        back={
          <Link to="/w/$workspace/contacts" params={params} search={{}}>
            <ArrowLeft /> Contacts
          </Link>
        }
      >
        <Button
          variant="outline"
          onClick={() =>
            downloadCsv("vienna-contacts-template.csv", [
              STANDARD_FIELDS.map((f) => f.key),
              ["Riley Quinn", "Riley", "riley@example.test", "Hackney", "Europe/London"],
            ])
          }
        >
          <Download /> Download template
        </Button>
      </Header>
      <div className="au-import-steps">
        <span>
          <Check className="size-3.5" /> Upload file
        </span>
        <ChevronRight className="size-3.5" />
        <strong>2. Map columns</strong>
        <ChevronRight className="size-3.5" />
        <span>3. Review and import</span>
      </div>
      <div className="au-two-column">
        <Section
          title="Map your columns"
          description="Choose how each column becomes a contact field."
        >
          <div className="au-file">
            <FileSpreadsheet className="size-5" />
            <div className="flex-1">
              <p className="text-sm font-medium">{imported.fileName}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {imported.rowCount.toLocaleString()} rows · {imported.columns.length} columns
              </p>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/w/$workspace/contacts/import" params={params}>
                Replace
              </Link>
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CSV column</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Vienna field</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imported.columns.map((column) => {
                  const restricted = imported.restrictedColumns.includes(column)
                  return (
                    <TableRow key={column}>
                      <TableCell className="font-sans text-xs">{column}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {imported.samples[column] || "(blank)"}
                      </TableCell>
                      <TableCell>
                        <SelectField
                          value={mapping[column] ?? "ignore"}
                          onChange={(value) => setMapping({ ...mapping, [column]: value })}
                          label={`Map ${column}`}
                          options={restricted ? RESTRICTED_TARGETS : targets}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
          {imported.restrictedColumns.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {imported.restrictedColumns.join(", ")}: consent-like columns can only be kept as a
              source note.
            </p>
          )}
        </Section>
        <Section title="Import behavior">
          <div className="space-y-5">
            <label className="flex items-start gap-3">
              <Checkbox checked disabled />
              <span>
                <span className="block text-sm font-medium">Create new person records</span>
                <span className="text-xs text-muted-foreground">
                  For rows without an exact match.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox
                checked={reviewMatches}
                onCheckedChange={(value) => setReviewMatches(value === true)}
              />
              <span>
                <span className="block text-sm font-medium">Review exact email matches</span>
                <span className="text-xs text-muted-foreground">
                  Review updates before applying them. Off skips every match.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox checked disabled />
              <span>
                <span className="block text-sm font-medium">Preserve existing permission</span>
                <span className="text-xs text-muted-foreground">
                  Imports never replace consent records.
                </span>
              </span>
            </label>
          </div>
          <Separator className="my-5" />
          <Hint>
            A “yes” value without source evidence doesn’t grant marketing permission. New profiles
            remain unknown.
          </Hint>
          <p className="mt-4 text-xs leading-5 text-muted-foreground">
            Imported handles are reference data. They don’t verify a channel identity or open a
            messaging window.
          </p>
        </Section>
      </div>
      <footer className="au-footer">
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/contacts/import" params={params}>
            <ArrowLeft /> Back
          </Link>
        </Button>
        <span className="mr-auto text-xs text-muted-foreground">
          {hasIdentity
            ? "Mapping is applied when you validate."
            : "Map a name or email column to continue."}
        </span>
        <Button disabled={!hasIdentity || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Validating…" : "Validate and review"}
          <ArrowRight />
        </Button>
      </footer>
    </div>
  )
}
